<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEdit, faPrint, faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { onMount } from 'svelte'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import dayjs from 'dayjs'
    import DateSelect from '../_common/date-select.svelte'
    import Popover from '../_common/popover.svelte'
    import FinancialForm from './financial-form.svelte'
    import type { Account } from '../../modules/financialApi/accounts/account'
    import type { Entry } from '../../modules/financialApi/entries/entry'
    import type { Category } from '../../modules/financialApi/categories/category'
    import { EntryTypeEnum } from '../../modules/financialApi/entries/entry.enum'

    let acounts: Account[] = []
    let account: Account = null

    let entries: Entry[] = []

    let persons: Person[] = []

    let show = false
    let dates: { from: Date; to: Date } = window.api.utils.dates.today()
    let total: number = 0
    let dateSelect

    onMount(async () => {
        await loadEntities()
    })

    interface Relat {
        category: Category
        total: number
        entries: Entry[]
        show: boolean
        debitCredit: EntryTypeEnum
    }

    let relat: Relat[] = []

    async function loadEntities() {
        try {
            show = true
            entries = []
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                accountId: account?.accountId || undefined,
            }

            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.financial.entries.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (diff > 7) {
                    dates.from = dayjs(new Date()).add(-7, 'day').toDate()
                    filter.from = dates.from
                    dateSelect.setDate(dates.from, dates.to)
                }
            }

            entries = await window.api.financials.entries.getAll(filter)
            persons = []
            let personsId = []
            for (const entry of entries) {
                personsId.push(entry.personId)
            }
            let promises = []
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                promises.push(window.api.events.persons.getAll({ persons: chunk.join(',') }))
            }
            const results = await Promise.all(promises)
            for (const result of results) {
                persons = persons.concat(result)
            }
            total = 0
            for (let entry of entries) {
                entry.person = persons.find((el) => el.personId === entry.personId)
                if (entry.type === EntryTypeEnum.credit) {
                    total += entry.value
                } else {
                    total -= entry.value
                }
            }

            relat = []
            let categories: Category[] = await window.api.financials.categories.getAll()
            for (const cat of categories) {
                const filtered = entries.filter((el) => el.categoryId === cat.categoryId)
                if (filtered.length > 0) {
                    let item: Relat = {
                        category: cat,
                        entries: filtered,
                        total: filtered.reduce((acc, el) => (acc += el.value), 0),
                        show: false,
                        debitCredit: filtered[0].type,
                    }
                    relat.push(item)
                }
            }
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    function showPerson(personId) {
        window.person('show', personId)
    }

    let showForm = false
    let entryId: string = null
    let creditDebit: EntryTypeEnum = null
    function editEntity(id) {
        entryId = id
        showForm = true
    }
    function formChanged() {
        loadEntities()
        showForm = false
    }
    function formCanceled() {
        loadEntities() // como podem ocorrer lançamentos em sequencia e depois o usuário sair clicando em "fechar", sempre atualizar
        showForm = false
    }
</script>

{#if showForm}
    <Popover title="Editar lançamento" width="1100px" height="360px" on:close={formCanceled}>
        <FinancialForm {entryId} {creditDebit} on:change={formChanged} on:cancel={formCanceled} />
    </Popover>
{/if}

<Content>
    <div class="grid-act">
        <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer h-mobile" />
        <button class="btn btn-secondary h-mobile" on:click={() => window.print()}><Fa icon={faPrint} /></button>
    </div>
    <div class="grid-body mb-4 financial">
        {#if !show}
            {#each Object.values(EntryTypeEnum) as debitCredit}
                <div class="row type-title">
                    <div class="col">{debitCredit === EntryTypeEnum.credit ? 'Entradas' : 'Saídas'}</div>
                    <div class="col-3 text-end">
                        Total {relat
                            .filter((el) => el.debitCredit === debitCredit)
                            .reduce((acc, el) => (acc += el.total), 0)
                            .toFixed(2)}
                    </div>
                </div>
                {#each relat.filter((el) => el.debitCredit === debitCredit) as item}
                    <div class:opened={item.show} class="row row-cat" on:click={() => (item.show = !item.show)}>
                        <div class="col nowrap">{item.category.label}</div>
                        <div class="col-2 text-end">
                            {item.total.toFixed(2)}

                            <span
                                >&nbsp;&nbsp;
                                {#if item.show}
                                    <Fa icon={faCaretUp} />
                                {:else}
                                    <Fa icon={faCaretDown} />
                                {/if}
                            </span>
                        </div>
                    </div>
                    <div class:hide={!item.show} class="extra-outer">
                        <div class="extra" id={'cat_' + item.category.categoryId}>
                            <div class="row extra-header text-muted">
                                <div class="col-1">Status</div>
                                <div class="col-1">Data</div>
                                <div class="col nowrap">&nbsp;Pessoa / Empresa</div>
                                <div class="col nowrap">Descrição</div>
                                <div class="col-2 text-end">Valor</div>
                                <div class="col-1 text-md-end h-print" />
                            </div>
                            {#each item.entries as entry}
                                <div class="row extra-item">
                                    <div class="col-1">
                                        <div class="badge status_{entry.status}">
                                            {entry.status === 100 ? 'Pago' : 'Em aberto'}
                                        </div>
                                    </div>
                                    <div class="col-1">{dayjs(entry.paymentAt).format('DD/MM/YYYY')}</div>
                                    <div class="col nowrap">&nbsp;{entry.person?.name}</div>
                                    <div class="col nowrap">{entry.description}</div>
                                    <div class="col-2 text-end">{entry.value.toFixed(2)}</div>
                                    <div class="col-1 text-md-end h-print">
                                        <button
                                            class="btn btn-secondary btn-sm"
                                            on:click={() => {
                                                editEntity(entry.entryId)
                                            }}
                                        >
                                            <Fa icon={faEdit} />
                                        </button>
                                    </div>
                                </div>
                            {/each}
                        </div>
                    </div>
                {:else}
                    <div class="row row-line-0">
                        <div class="col text-muted text-center">Nenhum valor lançado no período selecionado</div>
                    </div>
                {/each}
            {/each}
        {/if}
    </div>
    <LoadSpinner {show} />
</Content>

<style>
    .row-cat {
        cursor: pointer;
        background: #99999907;
    }
    .row-cat:hover {
        background: #99999915;
    }
    .hide {
        display: none;
    }

    .type-title {
        margin-top: 32px !important;
        font-size: 18px;
        background: #99999930;
    }

    .extra-outer {
        padding-left: 16px;
        padding-right: 16px;
        font-size: 13px;
        margin-bottom: 16px;
    }
    .opened {
        margin-top: 16px;
        background: #222;
    }
    .extra {
        border: 1px solid #222;
        border-top: 0px;
        background: #99999908;
    }
    .extra .btn {
        padding: 1px 8px !important;
    }
    .extra .extra-header {
        background: #99999908;
    }

    .extra .extra-item {
        color: #aaa;
    }

    @media (max-width: 900px) {
        .grid-act {
            background-color: unset;
            display: flex;
            justify-content: space-around;
        }
        .grid-act .btn {
            max-width: 120px;
            flex-grow: 1;
        }
    }
    @media (min-width: 1920px) {
    }

    @media print {
    }
</style>
